import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const SLOTS = [['morning','Morning'],['midday','Midday'],['evening','Evening'],['custom','Custom']];
const fmtStackTime = (t) => {
  if (!t) return 'No time set';
  const [h, m] = String(t).split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
};
const slotName = (s) => (SLOTS.find(x => x[0] === s) || ['','Morning'])[1];

const StackSettingsModal = ({ stack, endpoint, onClose, onSaved }) => {
  const [name, setName] = useState(stack.name || '');
  const [slot, setSlot] = useState(stack.default_slot || 'morning');
  const [time, setTime] = useState(stack.default_time || '');
  const [alertEnabled, setAlertEnabled] = useState(!!stack.default_alert);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return toast.error('Stack name required');
    setSaving(true);
    try {
      const { data } = await api.put(`${endpoint}/${stack.id}`, {
        name: name.trim(),
        default_slot: slot,
        default_time: time || null,
        default_alert: alertEnabled,
      });
      toast.success('Stack settings updated');
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 17 }}>Edit stack settings</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>&times;</button>
        </div>
        <label className="form-label">Stack name</label>
        <input className="form-input" value={name} onChange={e => setName(e.target.value)} />
        <label className="form-label" style={{ marginTop: 12 }}>Default feeding time</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-input" style={{ flex: 1 }} value={slot} onChange={e => setSlot(e.target.value)}>
            {SLOTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="form-input" style={{ flex: 1 }} type="time" value={time} onChange={e => setTime(e.target.value)} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
          <input type="checkbox" checked={alertEnabled} onChange={e => setAlertEnabled(e.target.checked)} />
          Show on Home notices at this time
        </label>
        <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>
    </div>
  );
};


const EXAMPLE_NAMES = 'e.g. Show String Grain, Senior Mash, Performance Ration';

// Add a feed item to a stack — searches the SEPARATE feed catalog only
const AddItemModal = ({ stackId, onClose, onSaved }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('lb');
  const [soaked, setSoaked] = useState(false);
  const [notes, setNotes] = useState('');
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [cycleDays, setCycleDays] = useState('30');
  const [leadDays, setLeadDays] = useState('5');
  const [trackUsage, setTrackUsage] = useState(true);
  const [requestPromotion, setRequestPromotion] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = useCallback((q) => {
    setSearching(true);
    api.get(`/feed/search?q=${encodeURIComponent(q)}`)
      .then(r => setResults(r.data.results || [])).catch(() => setResults([])).finally(() => setSearching(false));
  }, []);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const noMatch = query.trim() && !searching && results.length === 0;

  const handleSave = async () => {
    const name = picked ? picked.name : query.trim();
    if (!name) return toast.error('Choose or type a feed');
    setSaving(true);
    try {
      if (!picked) await api.post('/feed/private', { name, request_promotion: requestPromotion });
      await api.post(`/feed-stacks/${stackId}/items`, {
        name,
        subcategory: picked?.subcategory,
        amount,
        unit,
        soaked,
        notes,
        reorder_enabled: reorderEnabled,
        reorder_cycle_days: cycleDays,
        reorder_lead_days: leadDays,
        reorder_recurring: true,
        reorder_track_usage: trackUsage,
      });
      toast.success('Added to stack');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2>Add feed to stack</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        {!picked && (
          <>
            <div className="form-group">
              <label className="form-label">Search feed</label>
              <input className="form-input" value={query} onChange={e => setQuery(e.target.value)} placeholder="Start typing, e.g. Strategy" autoComplete="off" autoFocus />
            </div>
            {searching && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Searching…</div>}
            {results.length > 0 && (
              <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
                {results.map(r => (
                  <div key={r.id} onClick={() => setPicked(r)} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}{r.source === 'private' && <span style={{ fontSize: 10, color: 'var(--cr-primary)', marginLeft: 6 }}>Yours</span>}</div>
                    {r.subcategory && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.subcategory}</div>}
                  </div>
                ))}
              </div>
            )}
            {noMatch && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>No match for "<strong>{query.trim()}</strong>". Add it as your own item.</div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={requestPromotion} onChange={e => setRequestPromotion(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
                  Also request it be added to the master catalog
                </label>
              </div>
            )}
          </>
        )}
        {(picked || noMatch) && (
          <>
            <div style={{ background: 'var(--cr-primary-light)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{picked ? picked.name : query.trim()}</div>
              <button onClick={() => setPicked(null)} style={{ background: 'none', border: 'none', color: 'var(--cr-primary)', cursor: 'pointer', fontSize: 12, marginTop: 4, padding: 0 }}>Change</button>
            </div>
            <div className="form-group">
              <label className="form-label">Amount</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input className="form-input" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 2" inputMode="decimal" />
                <select className="form-input" value={unit} onChange={e => setUnit(e.target.value)}>
                  <option value="lb">Pounds (lb)</option>
                  <option value="scoop">Scoops</option>
                </select>
              </div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '4px 0 10px' }}>
              <input type="checkbox" checked={soaked} onChange={e => setSoaked(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
              Soaked
            </label>
            <div className="form-group"><label className="form-label">Notes (optional)</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. add warm water" /></div>
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: reorderEnabled ? 12 : 0 }}>
                <input type="checkbox" checked={reorderEnabled} onChange={e => setReorderEnabled(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
                Set up reorder reminder
              </label>
              {reorderEnabled && (
                <>
                  <div className="form-group">
                    <label className="form-label">Reorder every</label>
                    <input className="form-input" value={cycleDays} onChange={e => setCycleDays(e.target.value)} placeholder="30 days" inputMode="numeric" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Remind ahead</label>
                    <input className="form-input" value={leadDays} onChange={e => setLeadDays(e.target.value)} placeholder="5 days" inputMode="numeric" />
                  </div>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={trackUsage} onChange={e => setTrackUsage(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
                    Track usage — learns and adjusts timing from feedback.
                  </label>
                </>
              )}
            </div>
            <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Add to stack'}</button>
          </>
        )}
      </div>
    </div>
  );
};

const ApplyModal = ({ stack, horses, onClose, onApplied }) => {
  const [selected, setSelected] = useState('all');
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const handleApply = async () => {
    if (selected === 'selected' && picked.length === 0) return toast.error('Select at least one horse');
    setSaving(true);
    try {
      const { data } = await api.post(`/feed-stacks/${stack.id}/apply`, { horse_ids: selected === 'all' ? 'all' : picked });
      toast.success(data.message); onApplied();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2>Apply "{stack.name}"</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>Feed lands in each horse's feeding time at the stack's set time, joining hay & supplements already there. You can adjust each horse's amounts afterward on their own page — that won't change this saved stack.</p>
        <div className="chip-group" style={{ marginBottom: 12 }}>
          <div className={`chip ${selected === 'all' ? 'selected' : ''}`} onClick={() => setSelected('all')}>All horses</div>
          <div className={`chip ${selected === 'selected' ? 'selected' : ''}`} onClick={() => setSelected('selected')}>Select horses</div>
        </div>
        {selected === 'selected' && (
          <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 8, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
            {horses.map(h => (
              <label key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(h.id)} onChange={() => toggle(h.id)} style={{ accentColor: 'var(--cr-primary)' }} />
                {h.name}
              </label>
            ))}
          </div>
        )}
        <button className="btn btn-primary btn-full" onClick={handleApply} disabled={saving}>{saving ? 'Applying…' : 'Apply stack'}</button>
      </div>
    </div>
  );
};

const FeedStacksPage = () => {
  const navigate = useNavigate();
  const [stacks, setStacks] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newSlot, setNewSlot] = useState('morning');
  const [newTime, setNewTime] = useState('');
  const [newAlert, setNewAlert] = useState(false);
  const [openStack, setOpenStack] = useState(null);
  const [addingItem, setAddingItem] = useState(false);
  const [applying, setApplying] = useState(null);
  const [editingStack, setEditingStack] = useState(null);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([api.get('/feed-stacks'), api.get('/horses')]);
      setStacks(s.data); setHorses(h.data);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createStack = async () => {
    if (!newName.trim()) return toast.error('Name your stack');
    try {
      const { data } = await api.post('/feed-stacks', { name: newName, default_slot: newSlot, default_time: newTime, default_alert: newAlert });
      setNewName(''); setNewTime(''); setNewAlert(false);
      await load(); setOpenStack({ ...data, items: [] });
    } catch { toast.error('Failed'); }
  };
  const openStackDetail = async (id) => { try { const { data } = await api.get(`/feed-stacks/${id}`); setOpenStack(data); } catch { toast.error('Failed'); } };
  const deleteItem = async (itemId) => { try { await api.delete(`/feed-stacks/items/${itemId}`); openStackDetail(openStack.id); } catch { toast.error('Failed'); } };
  const deleteStack = async (id) => {
    if (!window.confirm('Delete this stack? Horses already set up keep their feed.')) return;
    try { await api.delete(`/feed-stacks/${id}`); setOpenStack(null); load(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (openStack) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
        <div className="top-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => { setOpenStack(null); load(); }} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
            <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>{openStack.name}</h1>
          </div>
        </div>
        <div style={{ padding: 12 }}>
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>&#9200;</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtStackTime(openStack.default_time)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {slotName(openStack.default_slot)} &middot; {openStack.default_alert ? 'Shows on Home notices' : 'Home notice off'}
              </div>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setEditingStack(openStack)}>Edit</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setAddingItem(true)}>+ Add feed</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setApplying(openStack)} disabled={(openStack.items || []).length === 0}>Apply to horses</button>
          </div>
          {(openStack.items || []).length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">🌾</div><h3>Empty stack</h3><p>Add the feed that makes up this program.</p></div>
          ) : (
            openStack.items.map(it => (
              <div key={it.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>🌾</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{it.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{[it.amount && `${it.amount} ${it.unit || 'lb'}`, it.soaked ? 'soaked' : null, it.notes, it.reorder_enabled ? `reorder ${it.reorder_cycle_days || 30}d` : null].filter(Boolean).join(' · ')}</div>
                </div>
                <button onClick={() => deleteItem(it.id)} className="btn btn-danger btn-sm">Remove</button>
              </div>
            ))
          )}
          <button className="btn btn-danger btn-full" style={{ marginTop: 16 }} onClick={() => deleteStack(openStack.id)}>Delete stack</button>
        </div>
        {addingItem && <AddItemModal stackId={openStack.id} onClose={() => setAddingItem(false)} onSaved={() => { setAddingItem(false); openStackDetail(openStack.id); }} />}
        {applying && <ApplyModal stack={applying} horses={horses} onClose={() => setApplying(null)} onApplied={() => setApplying(null)} />}
        {editingStack && <StackSettingsModal stack={editingStack} endpoint="/feed-stacks" onClose={() => setEditingStack(null)} onSaved={(u) => { setEditingStack(null); setOpenStack(prev => prev ? { ...prev, ...u, items: prev.items || [] } : prev); load(); }} />}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 40 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/horses')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>Feed stacks</h1>
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div className="card" style={{ background: 'var(--cr-primary-light)' }}>
          <p style={{ fontSize: 13 }}>Multiple horses on the same grain/feed program? Build a stack here and apply it to all or selected horses. After applying, open each horse to adjust amounts — those edits won't change the saved stack.</p>
        </div>

        <div className="card">
          <label className="form-label">New stack name</label>
          <input className="form-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder={EXAMPLE_NAMES} autoComplete="off" />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', margin: '6px 0 10px' }}>{EXAMPLE_NAMES}</div>
          <label className="form-label">Default feeding time (applied to each horse, editable after)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <select className="form-input" value={newSlot} onChange={e => setNewSlot(e.target.value)}>
              <option value="morning">Morning</option><option value="midday">Midday</option><option value="evening">Evening</option><option value="custom">Custom</option>
            </select>
            <input className="form-input" type="time" value={newTime} onChange={e => setNewTime(e.target.value)} />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, padding: '8px 0' }}>
            <input type="checkbox" checked={newAlert} onChange={e => setNewAlert(e.target.checked)} style={{ accentColor: 'var(--cr-primary)' }} />
            Set a feed reminder at this time
          </label>
          <button className="btn btn-primary btn-full" onClick={createStack}>+ Create stack</button>
        </div>

        {stacks.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">🌾</div><h3>No feed stacks yet</h3><p>Create your first feed program above, or reuse one for new horses.</p></div>
        ) : (
          stacks.map(s => (
            <div key={s.id} className="card" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }} onClick={() => openStackDetail(s.id)}>
              <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 18 }}>🌾</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{(s.items || []).length} feed item{(s.items || []).length !== 1 ? 's' : ''}</div>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: 20 }}>›</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FeedStacksPage;
